import React from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  AccountTreeOutlined,
  CategoryOutlined,
  ChevronRightOutlined,
  Inventory2Outlined,
  LayersOutlined,
  ShoppingCartOutlined,
} from '@mui/icons-material'

import { poolwerxColors } from '../../../../../theme/colors'
import { fmtInt } from '../../../../../config/tools/productCatalogueReview/formatters'
import type { Summary } from '../../../../../config/tools/productCatalogueReview/types'

interface SecondaryMetricCardProps {
  label: string
  value: string
  icon: React.ReactElement
  chips?: Array<{ label: string }>
  onOpen?: () => void
}

const SecondaryMetricCard: React.FC<SecondaryMetricCardProps> = ({
  label,
  value,
  icon,
  chips,
  onOpen,
}) => (
  <Box
    onClick={onOpen}
    sx={{
      flex: 1,
      minWidth: 0,
      py: 1,
      px: 0.75,
      borderRadius: 1.5,
      textAlign: 'center',
      cursor: onOpen ? 'pointer' : 'default',
      transition: 'background-color 120ms ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      '&:hover': onOpen
        ? {
            bgcolor: alpha(poolwerxColors.teal, 0.08),
            '& .tile-chevron': { opacity: 0.7 },
          }
        : undefined,
    }}
  >
    {onOpen && (
      <Box
        className="tile-chevron"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          color: 'text.secondary',
          opacity: 0,
          transition: 'opacity 120ms ease',
          pointerEvents: 'none',
          lineHeight: 0,
        }}
      >
        <ChevronRightOutlined sx={{ fontSize: 16 }} />
      </Box>
    )}
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      {React.cloneElement(icon, {
        sx: { fontSize: 14, color: 'text.secondary' },
      })}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontSize: '0.7rem',
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {chips && chips.length > 0 && (
      <Stack sx={{ mt: 0.5 }} alignItems="center">
        {chips.map((c) => (
          <Typography
            key={c.label}
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              fontWeight: 500,
            }}
          >
            {c.label}
          </Typography>
        ))}
      </Stack>
    )}
  </Box>
)

interface CatalogueKpiRowProps {
  summary: Summary
  onOpenVariants: () => void
  onOpenComposites: () => void
}

const CatalogueKpiRow: React.FC<CatalogueKpiRowProps> = ({
  summary,
  onOpenVariants,
  onOpenComposites,
}) => (
  <Stack
    direction="row"
    spacing={2}
    flexWrap="wrap"
    useFlexGap
    sx={{ mb: 3 }}
    alignItems="stretch"
  >
    {/* Products - totals + SPL */}
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 220,
        p: 2.5,
        borderRadius: 2,
        borderLeft: `4px solid ${poolwerxColors.teal}`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -12,
          bottom: -12,
          color: poolwerxColors.teal,
          opacity: 0.08,
          pointerEvents: 'none',
          lineHeight: 0,
        }}
      >
        <Inventory2Outlined sx={{ fontSize: 140 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          fontSize: '0.78rem',
          lineHeight: 1,
          mb: 1.25,
        }}
      >
        Products
      </Typography>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1.05,
          fontSize: '2.4rem',
          mb: 1,
        }}
      >
        {fmtInt(summary.total_products)}
      </Typography>
      <Stack direction="row" spacing={2.5} justifyContent="center">
        {[
          { label: 'Active', value: fmtInt(summary.active_products) },
          { label: 'Inactive', value: fmtInt(summary.inactive_products) },
          { label: 'PWX Std Prod List', value: fmtInt(summary.spl_products) },
        ].map((sm) => (
          <Box key={sm.label}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500, display: 'block', lineHeight: 1.2 }}
            >
              {sm.label}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              {sm.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>

    {/* Product Types - three tiles side by side */}
    <Paper
      variant="outlined"
      sx={{
        flex: 1.3,
        minWidth: 260,
        p: 2.5,
        borderRadius: 2,
        borderLeft: `4px solid ${poolwerxColors.teal}`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -12,
          bottom: -12,
          color: poolwerxColors.teal,
          opacity: 0.08,
          pointerEvents: 'none',
          lineHeight: 0,
        }}
      >
        <CategoryOutlined sx={{ fontSize: 140 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          fontSize: '0.78rem',
          lineHeight: 1,
          mb: 1.25,
          textAlign: 'center',
        }}
      >
        Product Types
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ flex: 1, pt: 0.5 }}
      >
        <SecondaryMetricCard
          label="Standard"
          value={fmtInt(summary.counts_by_product_type.simple)}
          icon={<Inventory2Outlined />}
        />
        <SecondaryMetricCard
          label="Variants"
          value={fmtInt(
            summary.counts_by_product_type.variant_parent +
              summary.counts_by_product_type.variant_child,
          )}
          icon={<AccountTreeOutlined />}
          chips={[
            { label: `${fmtInt(summary.variant_group_count)} Groups` },
          ]}
          onOpen={onOpenVariants}
        />
        <SecondaryMetricCard
          label="Composites"
          value={fmtInt(
            summary.counts_by_product_type.composite_dosage +
              summary.counts_by_product_type.composite_kit,
          )}
          icon={<LayersOutlined />}
          chips={[
            {
              label: `${fmtInt(
                summary.counts_by_product_type.composite_dosage,
              )} Dosage`,
            },
            {
              label: `${fmtInt(summary.counts_by_product_type.composite_kit)} Kit`,
            },
          ]}
          onOpen={onOpenComposites}
        />
      </Stack>
    </Paper>

    {/* Sold in period - narrower */}
    <Paper
      variant="outlined"
      sx={{
        flex: 0.85,
        minWidth: 200,
        p: 2.5,
        borderRadius: 2,
        borderLeft: `4px solid ${poolwerxColors.teal}`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -12,
          bottom: -12,
          color: poolwerxColors.teal,
          opacity: 0.08,
          pointerEvents: 'none',
          lineHeight: 0,
        }}
      >
        <ShoppingCartOutlined sx={{ fontSize: 140 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          fontSize: '0.78rem',
          lineHeight: 1,
          mb: 1.25,
        }}
      >
        Sold in period
      </Typography>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1.05,
          fontSize: '2.4rem',
          mb: summary.total_products > 0 ? 1.25 : 0,
        }}
      >
        {fmtInt(summary.sold_in_period)}
      </Typography>
      {summary.total_products > 0 && (
        <Stack direction="row" spacing={3} justifyContent="center">
          {[
            {
              label: '% of full list',
              value: `${Math.round(
                (summary.sold_in_period / summary.total_products) * 100,
              )}%`,
            },
            {
              label: 'Never sold',
              value: fmtInt(summary.never_sold_in_period),
            },
          ].map((sm) => (
            <Box key={sm.label}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500, display: 'block', lineHeight: 1.2 }}
              >
                {sm.label}
              </Typography>
              <Typography
                variant="body1"
                color="text.primary"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {sm.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  </Stack>
)

export default CatalogueKpiRow
